import { useEffect, useState } from "react";
import { KeyRound, Pencil, Plus, RefreshCw, Trash2, UserCog } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const empty = { name: "", email: "", password: "", role: "user", active: true };

export default function Accounts() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setRows((await api.get("/auth/accounts")).data || []); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const change = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const cancel = () => { setEditing(null); setForm(empty); };
  const edit = (row) => { setEditing(row.id); setForm({ name: row.name || "", email: row.email || "", password: "", role: row.role || "user", active: row.active !== false }); };
  const save = async (event) => {
    event.preventDefault(); setSaving(true);
    try {
      if (editing) await api.put(`/auth/accounts/${editing}`, { name: form.name, email: form.email, role: form.role, active: form.active });
      else await api.post("/auth/accounts", form);
      toast.success(editing ? "Account updated" : "Account created"); cancel(); await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const resetPassword = async (row) => {
    const password = window.prompt(`Enter a new password for ${row.email} (minimum 8 characters):`);
    if (!password) return;
    try { await api.post(`/auth/accounts/${row.id}/reset-password`, { new_password: password }); toast.success("Password reset"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete the account ${row.email}?`)) return;
    try { await api.delete(`/auth/accounts/${row.id}`); toast.success("Account deleted"); await load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return <div>
    <PageHeader title="User Accounts" sub="Create logins, edit access, reset passwords and disable users without changing your system configuration." right={<Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw size={15} /> Refresh</Button>} />
    <div className="grid xl:grid-cols-[380px_minmax(0,1fr)] gap-5 items-start">
      <form className="settings-card space-y-4" onSubmit={save}>
        <div><p className="eyebrow">{editing ? "Edit login" : "New login"}</p><h2 className="text-lg font-semibold mt-1">{editing ? "Update account" : "Add an account"}</h2></div>
        <label className="block"><span className="field-label">Name</span><Input required minLength={2} value={form.name} onChange={(e) => change("name", e.target.value)} /></label>
        <label className="block"><span className="field-label">Email</span><Input required type="email" value={form.email} onChange={(e) => change("email", e.target.value)} /></label>
        {!editing && <label className="block"><span className="field-label">Temporary password</span><Input required minLength={8} type="password" value={form.password} onChange={(e) => change("password", e.target.value)} /></label>}
        <label className="block"><span className="field-label">Role</span><select className="h-9 w-full rounded-sm border border-input bg-background px-2 text-sm" value={form.role} onChange={(e) => change("role", e.target.value)}><option value="user">User</option><option value="admin">Administrator</option></select></label>
        {editing && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => change("active", e.target.checked)} /> Account active</label>}
        <div className="flex gap-2"><Button type="submit" disabled={saving}><Plus size={15}/>{saving ? "Saving…" : editing ? "Save changes" : "Create account"}</Button>{editing && <Button type="button" variant="outline" onClick={cancel}>Cancel</Button>}</div>
      </form>
      <section className="settings-card overflow-hidden">
        <div className="flex items-center gap-2 mb-4"><UserCog size={19}/><div><h2 className="font-semibold">Existing accounts</h2><p className="text-xs text-muted-foreground">Administrators can manage accounts. Users can access the workspace but cannot manage other logins.</p></div></div>
        {loading ? <div className="py-12 text-center text-sm text-muted-foreground">Loading accounts…</div> : rows.length === 0 ? <div className="py-12 text-center text-sm text-muted-foreground">No accounts found.</div> : <div className="space-y-2">{rows.map((row) => <article key={row.id} className="panel p-3 flex flex-wrap items-center gap-3">
          <div className="min-w-[220px] flex-1"><b className="block">{row.name || "Unnamed user"}</b><span className="text-xs text-muted-foreground">{row.email}</span><div className="text-[11px] text-muted-foreground mt-1">Created {fmtDate(row.created_at)}</div></div>
          <span className={`text-xs border rounded-full px-2 py-1 ${row.active === false ? "text-destructive border-destructive/30" : "text-emerald-600 border-emerald-500/30"}`}>{row.active === false ? "Disabled" : "Active"}</span>
          <span className="text-xs border rounded-full px-2 py-1 capitalize">{row.role || "admin"}</span>
          <Button size="sm" variant="outline" onClick={() => edit(row)} title="Edit"><Pencil size={14}/></Button>
          <Button size="sm" variant="outline" onClick={() => resetPassword(row)} title="Reset password"><KeyRound size={14}/></Button>
          <Button size="sm" variant="outline" onClick={() => remove(row)} title="Delete"><Trash2 size={14}/></Button>
        </article>)}</div>}
      </section>
    </div>
  </div>;
}
